import React from 'react';
import { Button, Select, InputNumber } from '@appkit4/react-components';
import './DocumentFields.scss';

interface DocumentFieldsProps {
    fields: any[];
    onFieldUpdate: (field: any) => void;
    onFieldRemove: (fieldId: string) => void;
    onFieldAdd: () => void;
    onProceed: () => void;
    onNext: () => void;
    onViewPage: (page: number) => void;
    availableDocumentTypes?: any;
    totalPages: any;
    isSubmitting: boolean;
    isProceeding: boolean;
    hasNextDocument: boolean;
    documentView: boolean;
}

const DocumentFields: React.FC<DocumentFieldsProps> = ({
    fields,
    onFieldUpdate,
    onFieldRemove,
    onFieldAdd,
    onProceed,
    onNext,
    onViewPage,
    availableDocumentTypes = [],
    totalPages,
    isSubmitting,
    isProceeding,
    hasNextDocument,
    documentView,
}) => {
    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'AIApproved':
                return (
                    <span
                        className="Appkit4-icon icon-circle-checkmark-fill ap-font-24"
                        style={{ color: '#4EB523' }}
                    />
                );
            case 'AIRejected':
                return (
                    <span className="Appkit4-icon icon-warning-fill status-icon custom-warning-icon" />
                );
            default:
                return (
                    <span
                        className="status-dot pending-icon ap-font-24"
                        style={{ color: '#757575' }}
                    >
                        ●
                    </span>
                );
        }
    };

    const isRangeComplete = (range: { start: any; end: any }) => {
        return range.start !== null && range.end !== null;
    };

    const handleDocumentTypeChange = (field: any, documentTypeId: any) => {
        const selectedDocType = availableDocumentTypes.find(
            (type: any) => type.documentTypeId === documentTypeId
        );

        onFieldUpdate({
            ...field,
            documentTypeId: documentTypeId,
            documentType: selectedDocType?.documentTypeName || 'Unknown',
        });
    };
    const handleStartPageChange = (field: any, newValue: any) => {
        if (newValue === null || newValue === '') {
            onFieldUpdate({
                ...field,
                documentRange: {
                    ...field.documentRange,
                    start: null,
                },
            });
            return;
        }

        let start = Number(newValue);
        if (isNaN(start)) return;

        if (start < 1) start = 1;
        if (start > totalPages) start = totalPages;

        let end = Number(field.documentRange.end);
        if (isNaN(end) || end < start || end > totalPages) {
            end = start;
        }

        onFieldUpdate({
            ...field,
            documentRange: {
                start,
                end,
            },
        });
    };

    const handleEndPageChange = (field: any, newValue: any) => {
        if (newValue === null || newValue === '') {
            onFieldUpdate({
                ...field,
                documentRange: {
                    ...field.documentRange,
                    end: null,
                },
            });
            return;
        }

        let end = Number(newValue);
        if (isNaN(end)) return;

        if (end < 1) end = 1;
        if (end > totalPages) end = totalPages;

        let start = Number(field.documentRange.start);
        if (isNaN(start) || start > end || start > totalPages) {
            start = end;
        }

        onFieldUpdate({
            ...field,
            documentRange: {
                start,
                end,
            },
        });
    };

    return (
        <div className="document-fields">
            <div className="fields-header ">
                <div>Status</div>
                <div>Document type</div>
                <div>View page</div>
                <div>Document range</div>
            </div>
            {fields.map((field) => (
                <div key={field.id} className="field-row">
                    <div className="field-status">
                        {getStatusIcon(field.status)}
                    </div>
                    <div className="field-content">
                        <div className="document-type">
                            <Select
                                value={field.documentTypeId}
                                disabled={documentView}
                                onSelect={(value: any) =>
                                    handleDocumentTypeChange(field, value)
                                }
                                data={availableDocumentTypes.map(
                                    (type: any) => ({
                                        label: type.documentTypeName,
                                        value: type.documentTypeId,
                                    })
                                )}
                            />
                        </div>
                        <div
                            className={`view-page ${
                                !isRangeComplete(field.documentRange)
                                    ? 'disabled'
                                    : ''
                            }`}
                            onClick={() => {
                                if (
                                    isRangeComplete(field.documentRange) &&
                                    field.documentRange.start !== null
                                ) {
                                    onViewPage(field.documentRange.start);
                                }
                            }}
                            style={{
                                cursor: isRangeComplete(field.documentRange)
                                    ? 'pointer'
                                    : 'not-allowed',
                                opacity: isRangeComplete(field.documentRange)
                                    ? 1
                                    : 0.5,
                            }}
                        >
                            <span className="Appkit4-icon icon-view-outline ap-font-24"></span>
                        </div>
                        <div className="range">
                            <InputNumber
                                disabled={documentView}
                                value={field.documentRange.start}
                                min={1}
                                max={totalPages}
                                onChange={(val) =>
                                    handleStartPageChange(field, val)
                                }
                            />
                            <InputNumber
                                disabled={documentView}
                                value={field.documentRange.end}
                                min={1}
                                max={totalPages}
                                onChange={(val) =>
                                    handleEndPageChange(field, val)
                                }
                            />
                            <div
                                className="view-page"
                                onClick={() => onFieldRemove(field.id)}
                            >
                                <span className="Appkit4-icon icon-circle-delete-outline ap-font-24"></span>
                            </div>
                        </div>
                    </div>
                    {field.status === 'AIRejected' && (
                        <div className="confidence-message">
                            Low confidence so please check
                        </div>
                    )}
                </div>
            ))}
            <Button
                kind="secondary"
                icon="icon-circle-plus-outline"
                onClick={() => onFieldAdd()}
                className="add-field"
            >
                Add
            </Button>
            <div className="proceed-button-container">
                <Button
                    variant="primary"
                    onClick={onProceed}
                    className="proceed-button"
                    loading={isSubmitting}
                    disabled={isSubmitting || isProceeding || documentView}
                    icon="icon-save-outline"
                >
                    Submit
                </Button>
                <Button
                    variant="primary"
                    className="proceed-button"
                    onClick={onNext}
                    loading={isProceeding}
                    disabled={
                        isSubmitting ||
                        isProceeding ||
                        !hasNextDocument ||
                        documentView
                    }
                    icon="icon-arrow-right-outline"
                >
                    Submit & Proceed to Next
                </Button>
            </div>
        </div>
    );
};
export default DocumentFields;
